'use strict';

import * as debounce from 'lodash/debounce';
import { removeChildNodes, removeAccentCharacters, tryParseJSON } from 'Util/util';
import VZSpinner from 'Components/VZSpinner';
import { startsWithNumber, clearCompanyRelatedFields, getCompanyDetailsUrl } from './helpers/companySearchHelpers';
import { appendParamToURL, appendCsrfToUrl } from 'Util/urlUtils';
import moment from 'moment-timezone';
import { showChooserInviteSection } from 'Components/chooserInvite';
import { handleTransition } from 'Util/transitions.js';

/**
 * Strategy object for the checkout page
 * IMPORTANT: Company search is using cleanseMatch Search
 *
 * @property {Function} fetchCompanyDetails - Processes API response to set the KVK number in the lead form and update the checkout page UI
 * @property {Function} onSearchResultClick - Handles the click event on a single company search result item
 */
const checkoutStrategy = {
    /**
     * Processes retrieved company data to populate component fields and update the UI
     *
     * @param {Object} component - The calling component instance (passed as `this`)
     * @param {Object} res - The response object from the API containing the company data
     * @returns {void}
     */
    fetchCompanyDetails(component, res) {
        component.clearResults();
        component.populateCompanyNameField(res);
        const companyData = res?.companyData;
        // KVK
        if (companyData?.kvkNumber) {
            component.populateKvkField(res.companyData.kvkNumber);
        }

        // DUNS
        if (companyData?.resources?.duns) {
            component.setDunsField(companyData.resources.duns);
        }

        component.form.classList.add('d-none');
        component.result.classList.remove('d-none');

        // This event is used to enable or disable the checkout button in checkout.js when a company is changed. See BO-1648 for more info.
        component.result.dispatchEvent(
            new CustomEvent('shown:companySearchResult', {
                detail: { isEditing: false }
            })
        );
    },

    /**
     * Handles the click event on a single company search result item
     *
     * @param {Object} component - The parent component instance that contains the `handleCompanySelection` method
     * @param {HTMLElement} target - The clicked HTML element representing the search result
     * @returns {void}
     */
    onSearchResultClick(component, target) {
        const selectedKvkField = target.querySelector('.js-search-result-kvk');
        const selectedDunsField = target.querySelector('.js-search-result-duns');
        if (selectedKvkField) {
            // Extract the number from the string to populate kvk field if available
            const [kvkNumber] = selectedKvkField.textContent ? selectedKvkField.textContent.match(/\d+/) : [null];
            const dunsNumber = selectedDunsField && selectedDunsField.textContent;

            component.handleCompanySelection(kvkNumber, dunsNumber);
        }
    }
};

/**
 * Strategy object for all the pages that includes Form Strategy (Home/PLP/PDP/CART)
 * IMPORTANT: Company search is using Typeahead Search
 *
 * @property {Function} fetchCompanyDetails - Processes API response to set the KVK number specificaly when type Ahead Search is true
 * @property {Function} onSearchResultClick - Handles the click event on a single company search result item
 */
const leadFormStrategy = {
    /**
     * Processes retrieved company data
     *
     * Typeahead Search - doesn't return KVK number, We are making additional call to the
     * CompanyInfo-Profile to get KVK. This could be improved by creating GET 'subscriptions/account/' cloudsense call
     * and providing DUNS (instead of KVK - current behavior)
     *
     * @param {Object} component - The calling component instance (passed as `this`)
     * @param {Object} res - The response object from the API containing the company data
     * @returns {void}
     */
    fetchCompanyDetails(component, res) {
        const leadForm = component.closest('vz-lead-form');
        const kvkField = leadForm && leadForm.querySelector('.js-kvk-selected');
        if (kvkField) {
            kvkField.value = res.companyData.kvkNumber;
        }
    },
    /**
     * Handles a user's selection from a company search results list for a lead form.
     *
     * @param {Object} component - The calling component instance
     * @param {HTMLElement} target - The specific HTML element within the search results that was clicked
     * @returns {void}
     */
    onSearchResultClick(component, target) {
        // For Lead Form replace search query with selected company name
        const selectedNameField = target.querySelector('.js-business-name');
        const selectedKvkField = target.querySelector('.js-search-result-kvk');
        const selectedDunsField = target.querySelector('.js-search-result-duns');
        if (selectedNameField) {
            component.inputField.value = selectedNameField.textContent;
            if (selectedKvkField) {
                const leadForm = component.closest('vz-lead-form');
                const kvkField = leadForm && leadForm.querySelector('.js-kvk-selected');
                const dunsField = leadForm && leadForm.querySelector('.js-duns-selected');

                if (kvkField && selectedKvkField.textContent) {
                    kvkField.value = selectedKvkField.textContent.match(/\d+/g)?.join('') || '';
                    component.closeSearchResults();
                } else if (dunsField && selectedDunsField.textContent) {
                    dunsField.value = selectedDunsField.textContent || '';
                    component.handleCompanySelection(null, dunsField.value);
                }
            }
        } else {
            // Clear results on click on 'No results' option
            removeChildNodes(component.searchResults);
        }
    }
};

export default class VZCompanySearch extends HTMLElement {
    constructor() {
        super();
        this.inputField = this.querySelector('.js-company-search');
        this.form = this.querySelector('.js-company-search-section');
        this.result = this.querySelector('.js-company-result');
        this.searchResults = this.querySelector('.js-company-search-results');
        this.kvkNumberField = this.querySelector('.js-kvk-number');
        this.dunsNumberField = this.querySelector('.js-duns-number');
        this.companySelector = this.querySelector('.js-kvk-companies');
        this.chooserInviteElement = this.querySelector('.js-chooser-invite');
        this.companyInformationCards = ['companyInformation', 'paymentInformation', 'checkoutNavigation', 'installationInformation']
            .map((item) => document.getElementById(item))
            .filter((item) => item);

        this.minQueryLengthText = parseInt(this.inputField.dataset.minQueryLengthText, 10);
        this.minQueryLengthNumber = parseInt(this.inputField.dataset.minQueryLengthNumber, 10);
        this.tradenameEndpoint = this.inputField.dataset.tradenameUrl;
        this.kvkEndpoint = this.inputField.dataset.kvkUrl;
        this.hasExistingKvk = this.getAttribute('has-existing-kvk') === 'true';
        this.isLeadForm = this.getAttribute('is-lead-form') === 'true';
        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();
        this.lastSearchQuery = this.inputField.value || '';

        this.onEdit = this.onEdit.bind(this);
        this.onKeyup = this.onKeyup.bind(this);
        this.onInputKeydown = this.onInputKeydown.bind(this);
        this.onSearchResultClick = this.onSearchResultClick.bind(this);
        this.onSearchResultsKeydown = this.onSearchResultsKeydown.bind(this);
        this.handleSearchResultClick = this.handleSearchResultClick.bind(this);
        this.populateKvkField = this.populateKvkField.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.fetchExistingKvk = this.fetchExistingKvk.bind(this);
        this.getEndpoint = this.getEndpoint.bind(this);
        this.onExistingKvkFetch = this.onExistingKvkFetch.bind(this);

        // Set strategy Pattern based on attribute
        this.strategy = this.isLeadForm ? leadFormStrategy : checkoutStrategy;

        this.isProcessingExistingKvk = false; // Flag to prevent multiple simultaneous fetches when processing existing KVK on load
    }

    /**
     * @function observedAttributes
     * @description observes the mentioned attribute and fires attributeChangedCallback when the value has changed
     * @returns {Attr} the companies attribute. This attribute's value is an encoded JSON string so should be decoded when using.
     */
    static get observedAttributes() {
        return ['companies'];
    }

    connectedCallback() {
        this.inputField.addEventListener('keyup', debounce(this.onKeyup, 500));
        this.inputField.addEventListener('keydown', this.onInputKeydown);
        this.searchResults.addEventListener('click', this.onSearchResultClick);
        this.searchResults.addEventListener('keydown', this.onSearchResultsKeydown);
        document.addEventListener('click', this.handleDocumentClick);

        if (this.kvkNumberField && this.kvkNumberField.value.length > 0 && this.hasExistingKvk) {
            window.addEventListener('load', this.fetchExistingKvk);
        }

        if (this.querySelector('.js-company-edit')) this.querySelector('.js-company-edit').addEventListener('click', this.onEdit);
    }

    disconnectedCallback() {
        this.inputField.removeEventListener('keyup', this.onKeyup, 500);
        this.inputField.removeEventListener('keydown', this.onInputKeydown);
        this.searchResults.removeEventListener('click', this.onSearchResultClick);
        this.searchResults.removeEventListener('keydown', this.onSearchResultsKeydown);
        document.removeEventListener('click', this.handleDocumentClick);
        if (this.querySelector('.js-company-edit')) this.querySelector('.js-company-edit').removeEventListener('click', this.onEdit);
    }

    /**
     * @function attributeChangedCallback
     * @description fired when an observed attribute changes
     * @param {string} name the name of the attribute
     * @param {any} oldValue the previous value of the attribute
     * @param {any} newValue the new value of the attribute
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'companies' && this.hasExistingKvk) {
            this.onExistingKvkFetch(newValue);
        }
    }

    /**
     * @function closeSearchResults
     * @description Closes the search results dropdown
     * Removes the 'open' class from the main search element (`this.searchResults`),
     * Adds the 'd-none' utility class to the inner results container
     */
    closeSearchResults() {
        const resultsContainer = this.searchResults.querySelector('.js-company-search-results-container');
        this.searchResults.classList.remove('open');
        if (resultsContainer) {
            resultsContainer.classList.add('d-none');
        }
    }

    /**
     * @function handleDocumentClick
     * @private
     * @description handles closing the result list if a user clicks somewhere outside it and reopens if input is clicked again
     * @param {Event} evt the click event
     */
    handleDocumentClick(evt) {
        const { target } = evt;
        const resultsContainer = this.searchResults.querySelector('.js-company-search-results-container');

        if (target.classList.contains('has-error')) {
            target.classList.remove('has-error');
        }

        // open the dropdown
        if (target === this.inputField) {
            if (this.inputField.value.length === 0 || !resultsContainer) {
                this.closeSearchResults(); // Ensure it's closed if empty
                return;
            }
            const isNowOpen = this.searchResults.classList.toggle('open');
            resultsContainer.classList.toggle('d-none', !isNowOpen);

            // close the dropdown"
        } else if (!this.searchResults.contains(target)) {
            this.closeSearchResults();
        }
    }

    /**
     * @function handleSearchResultClick
     * @private
     * @description updates the HTML when a search result is clicked
     */
    handleSearchResultClick() {
        removeChildNodes(this.searchResults);
        this.inputField.value = '';
        this.inputField.dispatchEvent(new CustomEvent('input'));
    }

    /**
     * @function getSearchResultItems
     * @private
     * @returns {HTMLElement[]} interactive search result items
     */
    getSearchResultItems() {
        return [...this.searchResults.querySelectorAll('.js-company-search-result-item')];
    }

    /**
     * @function enhanceSearchResultsAccessibility
     * @private
     * @description Makes generated result rows keyboard-focusable and operable.
     */
    enhanceSearchResultsAccessibility() {
        this.getSearchResultItems().forEach((item) => {
            item.setAttribute('tabindex', '0');
            item.setAttribute('role', 'option');
        });
    }

    /**
     * @function onInputKeydown
     * @private
     * @description Handles autocomplete keyboard behavior from input field.
     * @param {KeyboardEvent} event keydown event on search input
     */
    onInputKeydown(event) {
        const hasOpenResults = this.searchResults.classList.contains('open');
        const resultItems = this.getSearchResultItems();

        if (event.key === 'Enter' || event.keyCode === 13) {
            event.preventDefault();
            return;
        }

        if (event.key === 'Escape') {
            if (!hasOpenResults) return;

            event.preventDefault();
            this.closeSearchResults();
            this.inputField.focus();
            return;
        }

        if (!hasOpenResults || !resultItems.length) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            resultItems[0].focus();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            resultItems[resultItems.length - 1].focus();
        }
    }

    /**
     * @function onSearchResultsKeydown
     * @private
     * @param {KeyboardEvent} evt keyboard event in search results list
     */
    onSearchResultsKeydown(evt) {
        const resultItem = evt.target.closest('.js-company-search-result-item');
        if (!resultItem) return;

        const resultItems = this.getSearchResultItems();
        if (!resultItems.length) return;

        if (evt.key === 'Escape') {
            evt.preventDefault();
            this.closeSearchResults();
            this.inputField.focus();
            return;
        }

        if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();
            resultItem.click();
            return;
        }

        const currentIndex = resultItems.indexOf(resultItem);
        if (currentIndex < 0) return;

        if (evt.key === 'ArrowDown') {
            evt.preventDefault();
            const nextIndex = Math.min(currentIndex + 1, resultItems.length - 1);
            resultItems[nextIndex].focus();
        } else if (evt.key === 'ArrowUp') {
            evt.preventDefault();
            const previousIndex = Math.max(currentIndex - 1, 0);
            resultItems[previousIndex].focus();
        }
    }

    /**
     * @function populateKvkField
     * @private
     * @param {string} value the kvk number
     */
    populateKvkField(value) {
        this.kvkNumberField.value = value;
        ['input', 'blur'].forEach((event) => this.kvkNumberField.dispatchEvent(new CustomEvent(event)));
    }

    /**
     * @function setDunsField
     * @private
     * @param {number} value the duns number
     */
    setDunsField(value) {
        this.dunsNumberField.value = value;
    }

    /**
     * @function clearResults
     * @private
     * @description resets the company selector dropdown
     */
    clearResults() {
        this.companySelector.options.length = 1;
        this.companySelector.options[0].removeAttribute('selected');
    }

    /**
     * @function populateCompanyNameField
     * @private
     * @description populates the company name field and triggers change which populates other related fields. See fn OnCompanyChange in checkout.js
     * @param {Object} data the data returned from the details endpoint
     */
    populateCompanyNameField(data) {
        const {
            companyData,
            companyData: { companyName },
            isExistingCompany
        } = data;
        const address = { ...companyData };
        const companies = [{ address, companyName }];

        // When the customer is unkown, enable the chooser information
        showChooserInviteSection(!isExistingCompany);

        this.companySelector.innerHTML = companies.reduce((result, company, index) => {
            if (!company.address?.country) company.address.country = 'NL';
            const stringifiedCompany = JSON.stringify(company).replace(/'/g, '&apos;'); // JSON.stringify does not escape single quotes so it needs to be done manually.

            return `${result}
                <option
                    data-value='${encodeURI(stringifiedCompany)}'
                    value="${company.companyName}"
                    ${index === 0 ? 'selected' : ''}>
                    ${company.companyName}
                </option>
            `;
        }, this.companySelector.innerHTML);

        const postcode = companies[0]?.address?.postalCode;
        const postcodeFormatted = postcode ? postcode.replace(/(^\d{4})([A-Za-z]{2})/, '$1 $2') : '';
        this.querySelector('.js-company-name').innerText = companies[0].companyName;
        this.querySelector('.js-company-address').innerText =
            `${companies[0].address.kvkNumber} - ${companies[0].address.street} ${companies[0].address.houseNumber}${companies[0].address.houseNumberAddition}, ${postcodeFormatted} ${companies[0].address.city}`;
        this.companyInformationCards.forEach((card) => card.setAttribute('visible', 'true'));

        ['change', 'blur'].forEach((event) => this.companySelector.dispatchEvent(new CustomEvent(event)));
        this.companySelector.classList.add('valid');
        const nowTime = moment().tz('Europe/Amsterdam').format('HH:mm');
        const currentTimeInput = document.querySelector('input[name=currentTime]');
        if (currentTimeInput) currentTimeInput.value = nowTime;
    }

    /**
     * @function fetchCompanyDetails
     * @private
     * @description fetches the selected company details on click of a search result
     * @param {string} url the endpoint url
     */
    fetchCompanyDetails(url) {
        this.spinner.start();

        fetch(url)
            .then((res) => res.json())
            .then((res) => {
                handleTransition(() => {
                    this.strategy.fetchCompanyDetails(this, res);
                });
            })
            .finally(this.spinner.stop);
    }

    /**
     * @function fetchCompanyList
     * @private
     * @description fetches the list of companies on entry in the search field
     * @param {string} url the endpoint url
     */
    fetchCompanyList(url) {
        this.spinner.start();

        fetch(url)
            .then((res) => res.json())
            .then((res) => {
                if (!res.success) throw res.serverErrors;

                this.inputField.removeAttribute('data-not-found-error');
                this.setAttribute('companies', encodeURI(JSON.stringify(res.companies)));

                if (res.companies.length === 1) {
                    const company = res.companies[0];
                    const kvkNumber = company.kvkNumber || (company.address && company.address.kvkNumber);

                    this.handleCompanySelection(kvkNumber);
                    return;
                }

                this.searchResults.innerHTML = res.html || this.constructor.generateResponseHTML(res);
                this.enhanceSearchResultsAccessibility();
                this.searchResults.classList.add('open');
            })
            .catch((res) => {
                const [title, body] = res;
                removeChildNodes(this.searchResults);
                this.searchResults.classList.add('open');

                // Add not-found-error Only for Checkout page
                if (!this.inputField.hasAttribute('data-pattern-error')) {
                    this.inputField.setAttribute('data-not-found-error', title);
                }

                this.searchResults.innerHTML = `
                    <div class="search-company-results-container bg-white border rounded-bottom position-relative js-company-search-results-container">
                        <ul class="list-group w-100">
                            <li class="search-company-results-item list-group-item rounded-0 font-weight-normal position-relative js-company-search-result-item py-1">
                                <h6 class="search-company-results-item-name m-0 pe-none">${title}</h6>
                                <p class="search-company-results-item-details m-0 text-gray-300 pe-none">${body}</p>
                            </li>
                        </ul>
                    </div>
                `;
                this.enhanceSearchResultsAccessibility();
            })
            .finally(this.spinner.stop);
    }

    /**
     * @function generateResponseHTML
     *
     * @param {JSON} response - Response from the CompanyInfo-Profile call
     * @return {HTMLElement} search result shown as HTML
     *
     * @description
     * Sync this file with cartridges/app_business_scan/cartridge/templates/default/businessScan/components/searchCompanyResults.isml
     */
    static generateResponseHTML(response) {
        const {
            companyData: { resources, companyName }
        } = response;
        return `
            <div class="search-company-results-container bg-white border rounded-bottom position-relative js-company-search-results-container">
                <h6 class="search-company-results-heading py-1 mb-0">
                    ${resources.header}
                </h6>

                <ul class="list-group w-100">
                    <li class="search-company-results-item list-group-item rounded-0 font-weight-normal position-relative js-company-search-result-item py-1">
                        <h6 class="search-company-results-item-name m-0 pe-none">
                            <strong>${companyName}</strong>
                        </h6>
                        <p class="search-company-results-item-details m-0 text-gray-300 pe-none">
                            <span class="js-search-result-kvk"><strong>${resources.kvk.split(' ').slice(0, 2).join(' ')}</strong></span>
                            <span>${resources.kvk.split(' ').slice(2).join(' ')}</span>
                            <span class="js-search-result-duns d-none">${resources.duns}</span>
                        </p>
                        <span class="d-none js-business-name">${companyName}</span>
                    </li>
                </ul>
            </div>
        `;
    }

    /**
     * @function handleCompanySelection
     * @private
     * @description Handles the selection of a company, either from click or single result
     * @param {string} kvkNumber - The KVK number of the selected company
     * @param {string} [dunsNumber] - Optional DUNS number of the selected company
     */
    handleCompanySelection(kvkNumber, dunsNumber) {
        // Extract companies from attribute
        const companies = tryParseJSON(decodeURI(this.getAttribute('companies')), []);

        // Create URL with company details
        let companyDetailsUrl = getCompanyDetailsUrl(companies, kvkNumber, dunsNumber);
        if (!this.isLeadForm) {
            companyDetailsUrl = appendParamToURL(companyDetailsUrl, 'investigate', true);
        }
        const selectedCompanyDetailsUrl = appendCsrfToUrl(companyDetailsUrl);

        // Handle UI updates
        if (this.isLeadForm) {
            // LeadForm
            this.closeSearchResults();
        } else {
            // Checkout
            this.handleSearchResultClick();
        }

        if (kvkNumber) {
            this.populateKvkField(kvkNumber);
        }

        // Fetch and populate company details
        this.fetchCompanyDetails(selectedCompanyDetailsUrl);
    }

    /**
     * @function onSearchResultClick
     * @private
     * @description handles clicking on a search result by setting calling the details URL for the selected result
     * @param {event} evt a click event on a search result
     */
    onSearchResultClick(evt) {
        const { target } = evt;
        this.strategy.onSearchResultClick(this, target);
    }

    /**
     * @function getEndpoint
     * @param {string} value - The value entered by a customer to search for.
     *
     * @return {string} The endpoint to call
     * @description
     * When a customer enters a trade name, the Company-Search controller should be called.
     * When a customer entered a KVK number, the Company-Profile endpoint should be called.
     */
    getEndpoint(value) {
        // Number constructor trims “0” from the beginning of a value.
        // so Number("0123456") becomes 123456 which leads to an incorrect KVK number
        return /^\d{8}$/.test(value) ? `${this.kvkEndpoint}?id=${value}&investigate=${!this.isLeadForm}` : `${this.tradenameEndpoint}?q=${value}`;
    }

    /**
     * @function fetchExistingKvk
     * @private
     * @description When a user lands on the page with an existing KVK this function triggers the search to populate companies
     */
    fetchExistingKvk() {
        const existingKvkNumber = this.kvkNumberField.value;
        if (!existingKvkNumber) return;

        this.isProcessingExistingKvk = true;

        const url = appendCsrfToUrl(this.getEndpoint(existingKvkNumber));
        this.fetchCompanyList(url).finally(() => {
            this.isProcessingExistingKvk = false; // Reset flag after processing
        });
    }

    /**
     * @function onExistingKvkFetch
     * @private
     * @description Called when companies attribute changes, this function fetches company details for an existing KVK number
     * @param {string} companies an encoded string of an array of companies - coming from attributeChangedCallback
     */
    onExistingKvkFetch(companies) {
        if (this.isProcessingExistingKvk) return; // Prevent duplicate

        const existingKvkNumber = this.kvkNumberField.value;
        if (!companies || !existingKvkNumber) return;
        let companyDetailsUrl = getCompanyDetailsUrl(tryParseJSON(decodeURI(companies), []), existingKvkNumber);
        if (!this.isLeadForm) {
            companyDetailsUrl = appendParamToURL(companyDetailsUrl, 'investigate', true);
        }
        const selectedCompanyDetailsUrl = appendCsrfToUrl(companyDetailsUrl);

        this.fetchCompanyDetails(selectedCompanyDetailsUrl).finally(() => {
            this.isProcessingExistingKvk = false; // Reset flag after processing
        });
    }

    /**
     * @function onKeyup
     * @private
     * @description handles entry in the search field. Calls company search endpoint and clears related fields.
     * @param {event} evt a keyup event on the search field
     */
    onKeyup(evt) {
        const {
            target: { value: searchQuery }
        } = evt;

        // Ignore keyup events that don't modify the search query (e.g. Escape / arrows).
        if (searchQuery === this.lastSearchQuery) return;
        this.lastSearchQuery = searchQuery;

        const minSearchQueryLength = startsWithNumber(searchQuery) ? this.minQueryLengthNumber : this.minQueryLengthText;
        const meetsMinLengthRequirement = searchQuery.length >= minSearchQueryLength;
        clearCompanyRelatedFields();

        if (meetsMinLengthRequirement) {
            const url = appendCsrfToUrl(this.getEndpoint(removeAccentCharacters(searchQuery)));
            this.fetchCompanyList(url);
        } else if (this.isLeadForm) {
            // Clear results if the query removed
            removeChildNodes(this.searchResults);
        }
    }

    onEdit() {
        // Clear client-side fields and UI
        this.form.classList.remove('d-none');
        this.result.classList.add('d-none');
        this.inputField.value = '';
        this.lastSearchQuery = '';
        this.clearResults();
        showChooserInviteSection(false);
        // Return keyboard users to the active search field.
        this.inputField.focus();

        // Clear server-side session data
        const editButton = this.querySelector('.js-company-edit');
        const clearSessionUrl = appendCsrfToUrl(editButton?.dataset.clearKvkUrl);

        this.spinner.start();

        const formData = new FormData();
        formData.append(window.csrf.tokenName, window.csrf.token);

        fetch(clearSessionUrl, {
            method: 'POST',
            body: formData
        })
            .then((response) => response.json())
            .then((data) => {
                if (!data.success) {
                    console.error('Failed to clear KVK session data:', data.error);
                }
                this.result.dispatchEvent(
                    new CustomEvent('shown:companySearchResult', {
                        detail: { isEditing: true }
                    })
                );
            })
            .catch((error) => {
                console.error('Error clearing KVK session data:', error);
                this.result.dispatchEvent(
                    new CustomEvent('shown:companySearchResult', {
                        detail: { isEditing: true }
                    })
                );
            })
            .finally(() => {
                this.spinner.stop();
            });
    }
}

if (!window.customElements.get('vz-company-search')) {
    window.customElements.define('vz-company-search', VZCompanySearch);
}
